import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Row, Col, Input, Card, Form, Radio } from 'antd';
import LoadingModal from 'components/generalUI/LoadingModal';
import ClientPurchases from 'components/PurchaseChange/ClientPurchases';
import useListChangeablePurchasesQuery from 'apollo/resolvers/purchases/listChangeablePurchases';
import useAuthenticateUserMutation from 'apollo/resolvers/clients/authenticateUser';
import emailRegExp from 'utils/validations/emailRegExp';
import dniValidator from 'utils/validations/dniValidator';
import rutFormValidator from 'utils/validations/rutFormValidator';
import rutFormat from 'utils/formats/rutFormat';
import Button from 'components/generalUI/Button';
import styles from './PurchaseChange.module.scss';
import { PurchaseChangeFormData } from './types';

const PurchaseChange: FC = () => {
  const { t } = useTranslation();

  const { token } = useParams<{ token: string }>();
  const [form] = Form.useForm();
  const { Item } = Form;

  const [userAuthenticationSuccess, setUserAuthenticationSuccess] =
    useState<boolean>(false);
  const [authenticateUser, { loading: isEmailVerificationLoading }] =
    useAuthenticateUserMutation();
  const [isPassport, setIsPassport] = useState<boolean>(false);

  const onFinish = (formData: PurchaseChangeFormData) => {
    const { email, documentIsPassport, documentNumber } = formData;
    authenticateUser({ variables: { email, documentIsPassport, documentNumber } })
      .then((response) => {
        if (response.data) {
          setUserAuthenticationSuccess(response.data.authenticateUser.success);
        } else {
          setUserAuthenticationSuccess(false);
        }
      })
      .catch(() => {
        setUserAuthenticationSuccess(false);
      });
  };

  const { data, loading: loadingPurchases } = useListChangeablePurchasesQuery(
    token,
    !token
  );

  const renderDocumentIsPassportField = () => {
    return (
      <Item name="documentIsPassport">
        <Radio.Group
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          onChange={(e) => setIsPassport(e.target.value)}
        >
          <Radio value={false}>{t('checkout.purchaseChange.rut')}</Radio>
          <Radio value>{t('checkout.purchaseChange.passport')}</Radio>
        </Radio.Group>
      </Item>
    );
  };

  const renderDocumentNumberField = () => {
    if (isPassport) {
      return (
        <Item
          name="documentNumber"
          rules={[
            { required: true, message: t('checkout.purchaseChange.warning') },
            { validator: dniValidator },
          ]}
        >
          <Input
            className={styles.FormInput}
            placeholder={`${t('checkout.purchaseChange.dni')} *`}
          />
        </Item>
      );
    }
    return (
      <Form.Item
        name="documentNumber"
        rules={[
          { required: true, message: t('checkout.purchaseChange.warning') },
          { validator: rutFormValidator },
        ]}
        normalize={rutFormat}
      >
        <Input
          className={styles.FormInput}
          placeholder={`${t('checkout.purchaseChange.rutExample')} *`}
        />
      </Form.Item>
    );
  };

  return (
    <div>
      <LoadingModal visible={isEmailVerificationLoading || loadingPurchases} />
      <Row justify="center">
        <Col>
          <h1 className={styles.Title}>{t('checkout.purchaseChange.title')}</h1>
        </Col>
      </Row>
      {data ? (
        <ClientPurchases
          changeablePurchases={data.listChangeablePurchases}
          token={token}
        />
      ) : (
        <>
          <Row justify="center">
            <Col xs={20} sm={18} lg={14} xl={12}>
              <Card className={styles.Card}>
                {userAuthenticationSuccess ? (
                  <>
                    <p>{t('checkout.purchaseChange.sentEmailText')}</p>
                  </>
                ) : (
                  <>
                    <p>{t('checkout.purchaseChange.enterData')}</p>
                    <Form
                      form={form}
                      onFinish={onFinish}
                      initialValues={{
                        documentIsPassport: false,
                      }}
                    >
                      <Item
                        name="email"
                        rules={[
                          {
                            required: true,
                            message: t('checkout.purchaseChange.warning'),
                          },
                          {
                            message: t('checkout.purchaseChange.invalidEmail'),
                            pattern: emailRegExp(),
                          },
                        ]}
                      >
                        <Input placeholder={t('checkout.purchaseChange.email')} />
                      </Item>
                      {renderDocumentIsPassportField()}
                      {renderDocumentNumberField()}
                      <Button
                        type="primary"
                        htmlType="submit"
                        className={styles.SubmitButton}
                      >
                        {t('checkout.purchaseChange.send')}
                      </Button>
                    </Form>
                  </>
                )}
              </Card>
            </Col>
          </Row>
          <Row justify="center">
            <Col className={styles.Footer} xs={20} sm={18} lg={14} xl={12}>
              <p>
                {t('checkout.purchaseChange.footerText')}
                <br />
                <br />
                {t('checkout.purchaseChange.footerTextAvailability')}
              </p>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default PurchaseChange;
